import React, { useEffect, useState } from "react";
import { createInvoice } from "@/services/invoices.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useEmailLookup } from "@/lib/useEmailLookup";

export const CreateInvoiceModal = ({ open, onClose, onCreated, defaultMilestoneId = null, prefill = {} }) => {
  const [form, setForm] = useState({
    title: "", description: "", amount: "", payer_name: "", payer_email: "", due_date: "",
    attachments_text: "", milestone_id: defaultMilestoneId, send_now: true, ...prefill,
  });
  const [saving, setSaving] = useState(false);
  const emailCheck = useEmailLookup(form.payer_email);
  const emailLooksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.payer_email || "").trim());

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, ...prefill, milestone_id: defaultMilestoneId ?? f.milestone_id }));
    // eslint-disable-next-line
  }, [open, defaultMilestoneId]);

  const submit = async () => {
    if (!form.title || !form.amount || !form.payer_email) { toast.error("Title, amount and payer email are required"); return; }
    if (emailCheck.self) { toast.error("You can't invoice yourself"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        amount: parseFloat(form.amount),
        payer_name: form.payer_name || null,
        payer_email: form.payer_email,
        due_date: form.due_date || null,
        attachments: (form.attachments_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
        milestone_id: form.milestone_id || null,
        send_now: !!form.send_now,
      };
      const data = await createInvoice(payload);
      toast.success(form.send_now ? "Invoice sent" : "Draft saved");
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="create-invoice-modal">
        <DialogHeader><DialogTitle>Request payment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Logo design – final" className="mt-1.5 h-11" data-testid="invoice-title-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Amount *</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="invoice-amount-input" />
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch {} }} className="mt-1.5 h-11 cursor-pointer" data-testid="invoice-due-input" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" data-testid="invoice-desc-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Payer name</Label>
              <Input value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} placeholder="Optional" className="mt-1.5 h-11" data-testid="invoice-payer-name-input" />
            </div>
            <div>
              <Label>Payer email *</Label>
              <Input type="email" value={form.payer_email} onChange={(e) => setForm({ ...form, payer_email: e.target.value })} placeholder="client@acme.com" className="mt-1.5 h-11" data-testid="invoice-payer-email-input" />
              {emailLooksValid && (
                emailCheck.checking ? (
                  <p className="mt-1.5 text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="invoice-email-check"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…</p>
                ) : emailCheck.self ? (
                  <p className="mt-1.5 text-xs text-red-500 inline-flex items-center gap-1.5" data-testid="invoice-email-check"><AlertCircle className="w-3.5 h-3.5" /> That's your own email.</p>
                ) : emailCheck.exists ? (
                  <p className="mt-1.5 text-xs text-primary inline-flex items-center gap-1.5" data-testid="invoice-email-check"><CheckCircle2 className="w-3.5 h-3.5" /> {emailCheck.name || "This user"} is on PAYD.</p>
                ) : (
                  <p className="mt-1.5 text-xs text-amber-500 inline-flex items-center gap-1.5" data-testid="invoice-email-check"><AlertCircle className="w-3.5 h-3.5" /> Not on PAYD yet. They'll get the invoice by email.</p>
                )
              )}
            </div>
          </div>
          <div>
            <Label>Attachments / links (one URL per line)</Label>
            <Textarea value={form.attachments_text} onChange={(e) => setForm({ ...form, attachments_text: e.target.value })} rows={2} placeholder="https://drive.google.com/…" className="mt-1.5 font-mono text-sm" data-testid="invoice-attachments-input" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm" data-testid="invoice-send-now-label">
            <input type="checkbox" checked={form.send_now} onChange={(e) => setForm({ ...form, send_now: e.target.checked })} className="accent-primary" />
            Send immediately (uncheck to save as Draft)
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="invoice-submit-btn">
            {saving ? "Saving…" : form.send_now ? (<><Send className="w-4 h-4 mr-1.5" /> Send invoice</>) : "Save draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvoiceModal;
